import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {KeyboardEventHandler} from 'react';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddPushableBlockStore} from './parts/AddPushableBlockStore';
import {DeletePushableBlockStore} from './parts/DeletePushableBlockStore';
import {EditPushableBlockStore} from './parts/EditPushableBlockStore';
import {ReorderPushableBlocksStore} from './parts/ReorderPushableBlocksStore';
import {X6PageLoader} from './X6PageLoader';

type Mode = 'View' | 'Add' | 'Edit' | 'Delete' | 'ReOrder';

export class X6PageStore {
  readonly loader: X6PageLoader;

  @observable
  mode: Mode;

  @observable.ref
  addPushableBlock: AddPushableBlockStore | null;
  @observable.ref
  editPushableBlock: EditPushableBlockStore | null;
  @observable.ref
  deletePushableBlock: DeletePushableBlockStore | null;
  @observable.ref
  reorder: ReorderPushableBlocksStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly pageId: number) {
    makeObservable(this);
    this.mode = 'View';

    this.loader = new X6PageLoader(pageId);
    this.disposers.add(this.loader);

    this.addPushableBlock = this.editPushableBlock =
      this.deletePushableBlock = this.reorder = null;

    this.setup();
  }

  @action
  private clear = () => {
    this.mode = 'View';
    this.addPushableBlock = this.editPushableBlock =
      this.deletePushableBlock = this.reorder = null;
  };

  @computed
  get isSaving(): boolean {
    return (this.addPushableBlock?.save?.isPending === true ||
      this.editPushableBlock?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.deletePushableBlock?.save?.isPending === true
    );
  }

  @action
  beginAdd = () => {
    this.mode = 'Add';
    this.addPushableBlock = new AddPushableBlockStore(this.pageId);
  };

  @action
  completeAdd = () => {
    this.addPushableBlock?.complete();
  };

  @action
  beginEdit = (pushableBlockId: number) => {
    this.mode = 'Edit';
    let pushableBlock = this.loader.pushableBlockById[pushableBlockId]!;
    this.editPushableBlock = new EditPushableBlockStore(pushableBlock);
  };

  @action
  completeEdit = () => {
    this.editPushableBlock?.complete();
  };

  @action
  completeOnEnter: KeyboardEventHandler = (event) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'Add')
      this.completeAdd();
    if (this.mode === 'Edit')
      this.completeEdit();
  };

  @action
  beginDelete = (pushableBlockId: number) => {
    this.mode = 'Delete';
    this.deletePushableBlock = new DeletePushableBlockStore(pushableBlockId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deletePushableBlock?.complete();
  };

  @action
  beginReorder = () => {
    this.mode = 'ReOrder';
    const pushableBlockIds = this.loader.pushableBlocks.map(x => x.Id);
    this.reorder = new ReorderPushableBlocksStore(this.pageId, pushableBlockIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorder?.swap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorder?.swap(index, index + 1);
  };

  @action
  completeReorder = () => {
    this.reorder?.complete();
  };

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.clear();
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addPushableBlock?.save?.state,
        this.editPushableBlock?.save?.state,
        this.deletePushableBlock?.save?.state,
        this.reorder?.save?.value
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addPushableBlock?.save,
      this.editPushableBlock?.save,
      this.deletePushableBlock?.save,
      this.reorder?.save,
    ])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.load();
        this.clear();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
